import { useState, useMemo } from 'react'
import { Box, IconButton, Typography, useTheme } from '@mui/material'
import * as Icons from '@mui/icons-material'
import { useTasksInOrg } from '../hooks/use-org-tasks'
import { applyScheduledState, filterTasksByDateOffset, DATE_OFFSET_STRATEGIES } from '../utils/task-grouping-utils'
import { parsePSQLIntervalToDays } from '../utils/task-interval-utils'
import { useCurrentUser } from '~/common/use-current-user'
import { TaskCalendarMonth } from './task-calendar-month'

const FALLBACK_READY_WINDOW_OFFSET_DAYS = 7

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * Returns true if a task overlaps the given month.
 */
const taskOverlapsMonth = (task, year, month) => {
  const monthStart = new Date(year, month, 1)
  const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999)
  const taskStart = new Date(task.task_window_start)
  const durationDays = parsePSQLIntervalToDays(task.duration)
  const taskEnd = new Date(taskStart)
  taskEnd.setDate(taskEnd.getDate() + Math.max(durationDays - 1, 0))
  return taskStart <= monthEnd && taskEnd >= monthStart
}

/**
 * Calendar view of tasks — fullscreen only.
 * Fetches tasks independently (cache-deduplicated with TaskList).
 */
export const TaskCalendar = () => {
  const theme = useTheme()
  const now = new Date()
  const [currentYear, setCurrentYear] = useState(now.getFullYear())
  const [currentMonth, setCurrentMonth] = useState(now.getMonth())

  const { tasks } = useTasksInOrg()
  const { userPreferences } = useCurrentUser()

  const filteredTasks = useMemo(() => {
    if (!tasks.length) return []

    const singleCompletionTasks = tasks.filter(t =>
      t.crop_id === null || t.task_types?.single_completion !== false
    )

    const scheduledTasks = applyScheduledState(
      singleCompletionTasks,
      userPreferences.ready_window_offset_days || FALLBACK_READY_WINDOW_OFFSET_DAYS
    )

    // Show all non-cancelled tasks for the calendar (no date-offset filter)
    return filterTasksByDateOffset(scheduledTasks, DATE_OFFSET_STRATEGIES.ALL)
      .filter(t => t.state !== 'cancelled')
  }, [tasks, userPreferences])

  const monthTasks = useMemo(
    () => filteredTasks.filter(t => taskOverlapsMonth(t, currentYear, currentMonth)),
    [filteredTasks, currentYear, currentMonth]
  )

  const handlePrev = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11)
      setCurrentYear(y => y - 1)
    } else {
      setCurrentMonth(m => m - 1)
    }
  }

  const handleNext = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0)
      setCurrentYear(y => y + 1)
    } else {
      setCurrentMonth(m => m + 1)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        px: 0.5,
      }}
    >
      {/* Month navigation header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          flexShrink: 0,
        }}
      >
        <IconButton size="small" onClick={handlePrev}>
          <Icons.ChevronLeft fontSize="small" />
        </IconButton>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {MONTH_NAMES[currentMonth]} {currentYear}
        </Typography>
        <IconButton size="small" onClick={handleNext}>
          <Icons.ChevronRight fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <TaskCalendarMonth
          year={currentYear}
          month={currentMonth}
          tasks={monthTasks}
        />
      </Box>
    </Box>
  )
}
